import { booleanAttribute, computed, Directive, forwardRef, inject, input, output } from '@angular/core';
import { ControlValueAccessor, FormControlDirective, FormControlName, NG_VALUE_ACCESSOR, NgControl, NgModel } from '@angular/forms';

export enum FormElementSize {
  Small = 'sm',
  Medium = 'md',
  Large = 'lg'
}

function validateElementSize(value: string) {
  const isValidSize = Object.values(FormElementSize as object).includes(value);
  return isValidSize ? value : FormElementSize.Medium;
}

@Directive({
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormElementDirective),
      multi: true
    }
  ]
})
export class FormElementDirective implements ControlValueAccessor {
  showErrorState = input(false);
  hideErrorMessage = input(false, { transform: booleanAttribute });
  placeholder = input<string>();

  size = input(FormElementSize.Medium, { transform: validateElementSize });

  blur = output<void>();
  focus = output<void>();

  onChange: () => void;
  onTouched: () => void;
  onDisabledChange: (disabled: boolean) => void;
  isDisabled: boolean;

  value: any;

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onBlur(): void {
    if (!!this.onTouched) {
      this.onTouched();
    }

    this.blur.emit();
  }

  onFocus(): void {
    if (!!this.onTouched) {
      this.onTouched();
    }

    this.focus.emit();
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;

    if (!!this.onDisabledChange) {
      this.onDisabledChange(this.isDisabled);
    } 
  }

   protected sizeClass = computed(() => {
    switch (this.size()) {
      case FormElementSize.Small:
        return 'is-small';
      case FormElementSize.Large:
        return 'is-large';
      case FormElementSize.Medium:
      default:
        return 'is-medium';
    }
  });
}

export function injectNgControl() {
  const ngControl = inject(NgControl, { self: true, optional: true });

  if (!ngControl) {
    throw new Error('Error injecting NgControl - No control detected');
  }

  if (ngControl instanceof FormControlDirective || ngControl instanceof FormControlName || ngControl instanceof NgModel) {
    return ngControl;
  }

  throw new Error('Error injecting NgControl - Invalid control instance');
}
